/**
 * IntegrationCard
 *
 * Card component for displaying and managing a single analytics integration.
 *
 * @module analytics-settings/components
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SDialog } from '@modules/ui-components';
import type { AnalyticsIntegration, PlatformConfig } from '../types/analytics.types';
import { PLATFORM_INFO } from '../types/analytics.types';
import { PlatformEditor } from './platform-editors';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

export interface IntegrationCardProps {
  integration: AnalyticsIntegration;
  onUpdate: (
    integrationId: string,
    updates: Partial<Omit<AnalyticsIntegration, 'id' | 'createdAt'>>
  ) => Promise<void>;
  onDelete: (integrationId: string) => Promise<void>;
  onToggle: (integrationId: string, enabled: boolean) => Promise<void>;
  disabled?: boolean;
  /** Project ID - required for server-side platforms (Facebook CAPI test events) */
  projectId?: string;
  /** All integrations in the project, including this one (FacebookPixelEditor D5 conflict check; other editors ignore it). */
  siblingIntegrations?: AnalyticsIntegration[];
}

/**
 * IntegrationCard - Single integration display and management
 */
export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  onUpdate,
  onDelete,
  onToggle,
  disabled = false,
  projectId,
  siblingIntegrations,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const platformInfo = PLATFORM_INFO[integration.platform];

  // Handle toggle
  const handleToggle = useCallback(async () => {
    await onToggle(integration.id, !integration.enabled);
  }, [integration.id, integration.enabled, onToggle]);

  // Handle menu
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  // Handle edit
  const handleEditClick = useCallback(() => {
    handleMenuClose();
    setEditOpen(true);
  }, [handleMenuClose]);

  const handleEditSave = useCallback(
    async (config: PlatformConfig, name?: string) => {
      await onUpdate(integration.id, {
        config,
        ...(name ? { name } : {}),
      });
      setEditOpen(false);
    },
    [integration.id, onUpdate]
  );

  // Handle delete
  const handleDeleteClick = useCallback(() => {
    handleMenuClose();
    setDeleteConfirmOpen(true);
  }, [handleMenuClose]);

  const handleDeleteConfirm = useCallback(async () => {
    await onDelete(integration.id);
    setDeleteConfirmOpen(false);
  }, [integration.id, onDelete]);

  // Get config summary for display
  const getConfigSummary = (): string => {
    const config = integration.config;
    switch (integration.platform) {
      case 'amplitude':
        return `API Key: ${(config as { apiKey?: string }).apiKey ? '••••' + (config as { apiKey: string }).apiKey.slice(-4) : 'Not set'}`;
      case 'ga4':
        return `Measurement ID: ${(config as { measurementId?: string }).measurementId || 'Not set'}`;
      case 'gtm':
        return `Container ID: ${(config as { containerId?: string }).containerId || 'Not set'}`;
      case 'mixpanel':
        return `Project Token: ${(config as { projectToken?: string }).projectToken ? '••••' + (config as { projectToken: string }).projectToken.slice(-4) : 'Not set'}`;
      case 'facebook_pixel':
      case 'tiktok_pixel':
        return `Pixel ID: ${(config as { pixelId?: string }).pixelId || 'Not set'}`;
      case 'custom_script': {
        const cs = config as { headScript?: string; bodyScript?: string };
        const headLines = (cs.headScript || '').split('\n').filter(Boolean).length;
        const bodyLines = (cs.bodyScript || '').split('\n').filter(Boolean).length;
        return `HEAD: ${headLines} lines | BODY: ${bodyLines} lines`;
      }
      default:
        return '';
    }
  };

  return (
    <>
      <Card
        variant="outlined"
        data-testid={`analytics-integration-card-${integration.id}`}
        data-platform={integration.platform}
        sx={{
          mb: 2,
          opacity: integration.enabled ? 1 : 0.6,
          transition: 'opacity 0.2s',
        }}
      >
        <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Platform Icon */}
            <Typography variant="h5" sx={{ width: 40, textAlign: 'center' }}>
              {platformInfo.icon}
            </Typography>

            {/* Platform Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {integration.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({platformInfo.name})
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" noWrap>
                {getConfigSummary()}
              </Typography>
            </Box>

            {/* Toggle */}
            <Switch
              checked={integration.enabled}
              onChange={handleToggle}
              disabled={disabled}
              size="small"
              data-testid={`analytics-integration-toggle-${integration.id}`}
              slotProps={{
                // Switch hard-codes slotProps.input = { role: 'switch' } and spreads it
                // AFTER the deprecated `inputProps` prop, so `inputProps` alone is
                // silently dropped here; `slotProps.input` is the only path that reaches
                // the actual <input>. Keep `role: 'switch'` so we don't regress a11y.
                input: {
                  role: 'switch',
                  'data-testid': ANALYTICS_SETTINGS_TEST_IDS.cardSwitch(integration.id),
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />

            {/* Expand Button */}
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
            >
              <ExpandMoreIcon />
            </IconButton>

            {/* Menu Button */}
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              disabled={disabled}
              data-testid={`analytics-integration-menu-${integration.id}`}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>

          {/* Expanded Content */}
          <Collapse in={expanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <PlatformEditor
                platform={integration.platform}
                config={integration.config}
                name={integration.name}
                onChange={() => {}} // Read-only in card view
                disabled={true}
                readOnly={true}
                projectId={projectId}
                siblingIntegrations={siblingIntegrations}
              />
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick} data-testid={ANALYTICS_SETTINGS_TEST_IDS.cardEdit(integration.id)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          sx={{ color: 'error.main' }}
          data-testid={`analytics-integration-delete-${integration.id}`}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <SDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Edit ${platformInfo.name}`}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <PlatformEditor
            platform={integration.platform}
            config={integration.config}
            name={integration.name}
            onChange={(config, name) => handleEditSave(config, name)}
            disabled={disabled}
            showSaveButton
            onSave={(config, name) => handleEditSave(config, name)}
            projectId={projectId}
            siblingIntegrations={siblingIntegrations}
          />
        </Box>
      </SDialog>

      {/* Delete Confirmation */}
      <SDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Integration"
        maxWidth="xs"
        actions={[
          {
            label: 'Cancel',
            onClick: () => setDeleteConfirmOpen(false),
          },
          {
            label: 'Delete',
            onClick: handleDeleteConfirm,
            color: 'error',
            variant: 'contained',
            'data-testid': 'analytics-integration-delete-confirm',
          },
        ]}
      >
        <Typography>
          Are you sure you want to delete the &quot;{integration.name}&quot; integration?
          This action cannot be undone.
        </Typography>
      </SDialog>
    </>
  );
};

export default IntegrationCard;
